import type { LucideIcon } from 'lucide-react';
import { CircleCheck, CircleX, TriangleAlert } from 'lucide-react';

export type InscricaoFeedbackOutcome = 'sucesso' | 'conflito' | 'falha';

export interface InscricaoFeedbackProps {
  outcome: InscricaoFeedbackOutcome;
  descricaoSimulado: string;
  className?: string;
}

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(' ');

interface Variant {
  icon: LucideIcon;
  role: 'status' | 'alert';
  border: string;
  background: string;
  ink: string;
  title: string;
  description: (descricaoSimulado: string) => string;
}

const VARIANTS: Record<InscricaoFeedbackOutcome, Variant> = {
  sucesso: {
    icon: CircleCheck,
    role: 'status',
    border: 'border-ok/40',
    background: 'bg-ok/[0.12]',
    ink: 'text-ok-ink',
    title: 'Inscrição confirmada',
    description: (descricaoSimulado) => `Você está inscrito em "${descricaoSimulado}".`,
  },
  conflito: {
    icon: TriangleAlert,
    role: 'status',
    border: 'border-warn/40',
    background: 'bg-warn/[0.12]',
    ink: 'text-warn-ink',
    title: 'Você já está inscrito',
    description: (descricaoSimulado) => `Você já tem uma inscrição ativa em "${descricaoSimulado}".`,
  },
  falha: {
    icon: CircleX,
    role: 'alert',
    border: 'border-danger/40',
    background: 'bg-danger/[0.12]',
    ink: 'text-danger-ink',
    title: 'A inscrição não foi concluída',
    description: (descricaoSimulado) =>
      `Não foi possível concluir sua inscrição em "${descricaoSimulado}". Tente novamente.`,
  },
};

export function InscricaoFeedback({ outcome, descricaoSimulado, className }: InscricaoFeedbackProps) {
  const variant = VARIANTS[outcome];
  const Icon = variant.icon;

  return (
    <div
      role={variant.role}
      className={cx(
        'flex items-start gap-3 rounded-card border px-4 py-3',
        variant.border,
        variant.background,
        className,
      )}
    >
      <Icon size={16} aria-hidden="true" className={cx('mt-0.5 shrink-0', variant.ink)} />
      <div className="min-w-0 flex-1">
        <p className={cx('m-0 text-sm font-semibold', variant.ink)}>{variant.title}</p>
        {/* break-words: descricaoSimulado is unbounded API text, must stay legible at 375px */}
        <p className="m-0 mt-0.5 text-sm text-ink-secondary break-words">
          {variant.description(descricaoSimulado)}
        </p>
      </div>
    </div>
  );
}
